import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday } from 'date-fns';
import { it, enUS } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock, List, LayoutGrid, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Calendar = ({ user, onLogout, theme, onToggleTheme }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [callbacks, setCallbacks] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const dateLocale = language === 'it' ? it : enUS;

  const view = searchParams.get('view') === 'list' ? 'list' : 'calendar';
  const listFilter = searchParams.get('filter'); // 'appointments' | 'callbacks' | null (both)

  useEffect(() => {
    fetchAppointments();
    fetchCallbacks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, view]);

  const fetchAppointments = async () => {
    try {
      const token = localStorage.getItem('token');
      let startIso, endIso;
      if (view === 'list') {
        // In list view, fetch a wide window (past 30 days + next 365 days)
        const start = new Date();
        start.setDate(start.getDate() - 30);
        const end = new Date();
        end.setDate(end.getDate() + 365);
        startIso = start.toISOString();
        endIso = end.toISOString();
      } else {
        const start = startOfMonth(currentDate);
        const end = endOfMonth(currentDate);
        startIso = new Date(start.getFullYear(), start.getMonth(), start.getDate() - 1).toISOString();
        endIso = new Date(end.getFullYear(), end.getMonth(), end.getDate() + 1, 23, 59, 59).toISOString();
      }
      const response = await axios.get(
        `${API}/appointments?start_date=${encodeURIComponent(startIso)}&end_date=${encodeURIComponent(endIso)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAppointments(response.data);
    } catch (error) {
      console.error('Error fetching appointments:', error);
    }
  };

  const fetchCallbacks = async () => {
    try {
      const token = localStorage.getItem('token');
      // In list view we want all statuses; in calendar view only pending is meaningful
      const url = view === 'list' ? `${API}/callbacks` : `${API}/callbacks?status=pending`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCallbacks(response.data);
    } catch (error) {
      console.error('Error fetching callbacks:', error);
    }
  };

  const setView = (nextView, filter) => {
    const params = new URLSearchParams(searchParams);
    if (nextView === 'list') {
      params.set('view', 'list');
      if (filter) params.set('filter', filter);
      else params.delete('filter');
    } else {
      params.delete('view');
      params.delete('filter');
    }
    setSearchParams(params);
  };

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const getEventsForDate = (date) => {
    const appts = appointments.filter((appt) =>
      isSameDay(new Date(appt.appointment_date), date)
    );
    const cbs = callbacks.filter((cb) =>
      isSameDay(new Date(cb.callback_date), date)
    );
    return { appointments: appts, callbacks: cbs };
  };

  const selectedEvents = getEventsForDate(selectedDate);

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar
        user={user}
        onLogout={onLogout}
        theme={theme}
        onToggleTheme={onToggleTheme}
        collapsed={collapsed}
        onToggle={() => setCollapsed(!collapsed)}
      />
      <div
        className="flex-1 overflow-y-auto"
        style={{ backgroundColor: theme === 'dark' ? '#0A0A0A' : '#F8F9FA' }}
      >
        <div className="p-6 lg:p-8">
          <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1
                className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-2"
                style={{ color: theme === 'dark' ? '#F4F4F4' : '#161616' }}
                data-testid="calendar-title"
              >
                {view === 'list'
                  ? (listFilter === 'appointments'
                      ? (t('list_appointments_title') || 'Clienti con Appuntamento')
                      : listFilter === 'callbacks'
                        ? (t('list_callbacks_title') || 'Clienti da Richiamare')
                        : (t('list_all_title') || 'Lista Clienti'))
                  : t('calendar_title')}
              </h1>
              <p className="text-base" style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}>
                {view === 'list'
                  ? (t('list_subtitle') || 'Elenco completo con nome, telefono e note')
                  : t('calendar_subtitle')}
              </p>
            </div>
            <div className="flex gap-2 rounded-md p-1" style={{ backgroundColor: theme === 'dark' ? '#161616' : '#FFFFFF', border: `1px solid ${theme === 'dark' ? '#262626' : '#E0E0E0'}` }}>
              <Button
                size="sm"
                variant={view === 'calendar' ? 'default' : 'ghost'}
                onClick={() => setView('calendar')}
                data-testid="calendar-view-toggle"
                className={view === 'calendar' ? 'btn-primary' : ''}
              >
                <LayoutGrid size={16} className="mr-2" />
                {t('view_calendar') || 'Calendario'}
              </Button>
              <Button
                size="sm"
                variant={view === 'list' ? 'default' : 'ghost'}
                onClick={() => setView('list', listFilter || null)}
                data-testid="list-view-toggle"
                className={view === 'list' ? 'btn-primary' : ''}
              >
                <List size={16} className="mr-2" />
                {t('view_list') || 'Lista'}
              </Button>
            </div>
          </div>

          {view === 'list' && (
            <div className="mb-4 flex gap-2 flex-wrap" data-testid="list-filter-tabs">
              <Button
                size="sm"
                variant={!listFilter ? 'default' : 'outline'}
                className={!listFilter ? 'btn-primary' : ''}
                onClick={() => setView('list', null)}
                data-testid="list-filter-all"
              >
                {t('list_filter_all') || 'Tutti'}
                <span className="ml-2 opacity-70">({appointments.length + callbacks.length})</span>
              </Button>
              <Button
                size="sm"
                variant={listFilter === 'appointments' ? 'default' : 'outline'}
                className={listFilter === 'appointments' ? 'btn-primary' : ''}
                onClick={() => setView('list', 'appointments')}
                data-testid="list-filter-appointments"
              >
                <CalendarIcon size={14} className="mr-2" />
                {t('list_filter_appointments') || 'Appuntamenti'}
                <span className="ml-2 opacity-70">({appointments.length})</span>
              </Button>
              <Button
                size="sm"
                variant={listFilter === 'callbacks' ? 'default' : 'outline'}
                className={listFilter === 'callbacks' ? 'btn-primary' : ''}
                onClick={() => setView('list', 'callbacks')}
                data-testid="list-filter-callbacks"
              >
                <Clock size={14} className="mr-2" />
                {t('list_filter_callbacks') || 'Da richiamare'}
                <span className="ml-2 opacity-70">({callbacks.length})</span>
              </Button>
            </div>
          )}

          {view === 'list' ? (
            <div
              className="rounded-lg border overflow-hidden"
              style={{
                backgroundColor: theme === 'dark' ? '#161616' : '#FFFFFF',
                borderColor: theme === 'dark' ? '#262626' : '#E0E0E0',
              }}
              data-testid="clients-list-view"
            >
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead style={{ backgroundColor: theme === 'dark' ? '#0A0A0A' : '#F8F9FA', borderBottom: `1px solid ${theme === 'dark' ? '#262626' : '#E0E0E0'}` }}>
                    <tr>
                      {[t('col_outcome') || 'Esito', t('col_name') || 'Cliente', t('col_phone') || 'Telefono', t('list_col_datetime') || 'Data e ora', t('col_notes') || 'Note', t('col_owner') || 'Operatore', t('actions') || 'Azioni'].map((label) => (
                        <th key={label} className="px-4 py-3 text-left text-xs uppercase tracking-wider whitespace-nowrap" style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}>{label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const rows = [];
                      if (!listFilter || listFilter === 'appointments') {
                        appointments.forEach((a) => rows.push({ kind: 'appointment', id: a.id, lead_id: a.lead_id, name: a.lead_name, phone: a.lead_phone, date: a.appointment_date, note: a.note, user_name: a.user_name }));
                      }
                      if (!listFilter || listFilter === 'callbacks') {
                        callbacks.forEach((c) => rows.push({ kind: 'callback', id: c.id, lead_id: c.lead_id, name: c.lead_name, phone: c.lead_phone, date: c.callback_date, note: c.reason, user_name: c.user_name }));
                      }
                      rows.sort((a, b) => new Date(a.date) - new Date(b.date));
                      if (rows.length === 0) {
                        return (
                          <tr>
                            <td colSpan={7} className="px-4 py-10 text-center" style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }} data-testid="list-empty-state">
                              {t('list_empty') || 'Nessun cliente con questo esito.'}
                            </td>
                          </tr>
                        );
                      }
                      return rows.map((r) => (
                        <tr key={`${r.kind}-${r.id}`} style={{ borderBottom: `1px solid ${theme === 'dark' ? '#262626' : '#E0E0E0'}` }} data-testid={`list-row-${r.kind}-${r.id}`}>
                          <td className="px-4 py-3">
                            <span className="px-2 py-1 rounded text-xs font-medium inline-flex items-center gap-1" style={{ backgroundColor: r.kind === 'appointment' ? '#19803815' : '#F1C21B15', color: r.kind === 'appointment' ? '#198038' : '#F1C21B' }}>
                              {r.kind === 'appointment' ? <CalendarIcon size={12} /> : <Clock size={12} />}
                              {r.kind === 'appointment' ? (t('outcome_appointment') || 'Appuntamento') : (t('outcome_callback') || 'Da richiamare')}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-medium" style={{ color: theme === 'dark' ? '#F4F4F4' : '#161616' }}>{r.name}</td>
                          <td className="px-4 py-3 font-mono text-xs" style={{ color: '#0F62FE' }}>
                            <Phone size={12} className="inline mr-1" />{r.phone}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap" style={{ color: theme === 'dark' ? '#F4F4F4' : '#161616' }}>
                            {format(new Date(r.date), 'dd/MM/yyyy HH:mm', { locale: dateLocale })}
                          </td>
                          <td className="px-4 py-3 text-xs max-w-xs truncate" title={r.note || ''} style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}>{r.note || '-'}</td>
                          <td className="px-4 py-3 text-xs" style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}>{r.user_name || '-'}</td>
                          <td className="px-4 py-3">
                            <Button size="sm" variant="ghost" onClick={() => navigate(`/leads/${r.lead_id}`)} data-testid={`list-open-lead-${r.lead_id}`}>
                              {t('open_lead') || 'Apri Lead'}
                            </Button>
                          </td>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div
              className="lg:col-span-2 p-6 rounded-lg border"
              style={{
                backgroundColor: theme === 'dark' ? '#161616' : '#FFFFFF',
                borderColor: theme === 'dark' ? '#262626' : '#E0E0E0',
              }}
            >
              <div className="flex items-center justify-between mb-6">
                <h2
                  className="text-2xl font-bold"
                  style={{ color: theme === 'dark' ? '#F4F4F4' : '#161616' }}
                >
                  {format(currentDate, 'MMMM yyyy', { locale: dateLocale })}
                </h2>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() - 1)))}
                    data-testid="calendar-prev-month"
                  >
                    <ChevronLeft size={20} />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setCurrentDate(new Date())}
                    data-testid="calendar-today"
                  >
                    {t('today')}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() + 1)))}
                    data-testid="calendar-next-month"
                  >
                    <ChevronRight size={20} />
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-7 gap-2">
                {(language === 'it' ? ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'] : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']).map((day) => (
                  <div
                    key={day}
                    className="text-center text-xs uppercase tracking-wider py-2"
                    style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}
                  >
                    {day}
                  </div>
                ))}
                {daysInMonth.map((day) => {
                  const events = getEventsForDate(day);
                  const hasEvents = events.appointments.length > 0 || events.callbacks.length > 0;
                  const isSelected = isSameDay(day, selectedDate);
                  const isTodayDate = isToday(day);

                  return (
                    <button
                      key={day.toString()}
                      onClick={() => setSelectedDate(day)}
                      className="p-2 rounded-md transition-colors relative"
                      style={{
                        backgroundColor: isSelected
                          ? '#0F62FE'
                          : isTodayDate
                          ? theme === 'dark'
                            ? '#262626'
                            : '#E8EAED'
                          : 'transparent',
                        color: isSelected
                          ? '#FFFFFF'
                          : theme === 'dark'
                          ? '#F4F4F4'
                          : '#161616',
                        border: isTodayDate && !isSelected ? `2px solid #0F62FE` : 'none',
                      }}
                      data-testid={`calendar-day-${format(day, 'yyyy-MM-dd')}`}
                    >
                      <div className="text-sm font-medium">{format(day, 'd')}</div>
                      {hasEvents && (
                        <div className="flex gap-1 justify-center mt-1">
                          {events.appointments.length > 0 && (
                            <div
                              className="w-1.5 h-1.5 rounded-full"
                              style={{ backgroundColor: '#198038' }}
                            />
                          )}
                          {events.callbacks.length > 0 && (
                            <div
                              className="w-1.5 h-1.5 rounded-full"
                              style={{ backgroundColor: '#F1C21B' }}
                            />
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <div
              className="p-6 rounded-lg border"
              style={{
                backgroundColor: theme === 'dark' ? '#161616' : '#FFFFFF',
                borderColor: theme === 'dark' ? '#262626' : '#E0E0E0',
              }}
            >
              <h3
                className="text-xl font-bold mb-4"
                style={{ color: theme === 'dark' ? '#F4F4F4' : '#161616' }}
              >
                {format(selectedDate, 'd MMMM yyyy', { locale: dateLocale })}
              </h3>

              <div className="space-y-4">
                {selectedEvents.appointments.length === 0 && selectedEvents.callbacks.length === 0 ? (
                  <p style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}>{t('no_events')}</p>
                ) : (
                  <>
                    {selectedEvents.appointments.map((appt) => (
                      <div
                        key={appt.id}
                        className="p-4 rounded-md border"
                        style={{
                          backgroundColor: '#19803815',
                          borderColor: '#198038',
                        }}
                        data-testid={`appointment-${appt.id}`}
                      >
                        <div className="flex items-start gap-2 mb-2">
                          <CalendarIcon size={16} style={{ color: '#198038' }} />
                          <div className="flex-1">
                            <p className="font-medium" style={{ color: theme === 'dark' ? '#F4F4F4' : '#161616' }}>
                              {appt.lead_name}
                            </p>
                            <p className="text-sm font-mono" style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}>
                              {appt.lead_phone}
                            </p>
                          </div>
                        </div>
                        <p className="text-sm" style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}>
                          {format(new Date(appt.appointment_date), 'HH:mm', { locale: dateLocale })}
                        </p>
                        {appt.note && (
                          <p className="text-sm mt-2" style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}>
                            {appt.note}
                          </p>
                        )}
                      </div>
                    ))}

                    {selectedEvents.callbacks.map((cb) => (
                      <div
                        key={cb.id}
                        className="p-4 rounded-md border"
                        style={{
                          backgroundColor: '#F1C21B15',
                          borderColor: '#F1C21B',
                        }}
                        data-testid={`callback-${cb.id}`}
                      >
                        <div className="flex items-start gap-2 mb-2">
                          <Clock size={16} style={{ color: '#F1C21B' }} />
                          <div className="flex-1">
                            <p className="font-medium" style={{ color: theme === 'dark' ? '#F4F4F4' : '#161616' }}>
                              {cb.lead_name}
                            </p>
                            <p className="text-sm font-mono" style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}>
                              {cb.lead_phone}
                            </p>
                          </div>
                        </div>
                        <p className="text-sm" style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}>
                          {format(new Date(cb.callback_date), 'HH:mm', { locale: dateLocale })}
                        </p>
                        {cb.reason && (
                          <p className="text-sm mt-2" style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}>
                            {cb.reason}
                          </p>
                        )}
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Calendar;
